import { Application, Container, Graphics } from 'pixi.js';
import { el, clear, toast } from '../dom';
import { setLeaveGuard, clearLeaveGuard } from '../leave-guard';
import type { GameCtx, ViewTeardown } from '../types';
import { ARENA_TILE } from '../arena/render';
import { armourLayerFile } from '../ui/armor-art';
import { armourPaintColor } from '../map/ores';
import { gearedDamage, canStrike, rollBotGear, rollHit, NO_GEAR, type ArenaGear } from '../arena/combat';
import { buildArenaField, buildFieldNav, ARENA_BORDER_BAND, type ArenaField, type FieldNav } from '../arena/field';
import { createArenaHero, ARENA_ANCHOR, type ArenaHero } from '../arena/hero';
import { pxOfTile, tileOfPx } from '../arena/coords';
import { computeAvoidance, SEP_FORCE, type AvoidAgent } from '../nav/avoidance';
import { buildFighters, soloSeats, type MatchSeat, type Fighter } from '../arena/names';
import { randomAppearance, defaultAppearance, type Appearance } from '../character/compositor';
import { loadAppearance } from '../character/appearance-store';
import { craftingApi } from '../game-api';
import { mulberry32 } from '../map/prng';
import { NavService } from '../nav/service';
import { tileIndex } from '../nav/grid';
import {
  writeMatchSnapshot, clearMatchSnapshot, catchUpMs,
  type MatchSnapshot, type HeroSnap, type CombatSnap,
} from '../arena/resume';

// Pixel-art icons (sliced from the Cute Fantasy pack) replacing emoji in the match UI.
const AICON = { coin: '/sprites/ui/currency/coin.png', shroom: '/sprites/ui/currency/shroom.png', swords: '/sprites/ui/arena/swords.png', star: '/sprites/ui/arena/star.png' };
const aicon = (src: string, h = 14) =>
  el('img', 'inline-block align-[-0.18em] shrink-0', { src, alt: '', style: `height:${h}px;width:auto;image-rendering:pixelated` });
import { TILE } from '../map/world';
import { FOOT_OFFSET } from '../map/player';
import type { Dir } from '../map/player';
import { createJoystick, isTouchDevice, type Joystick } from '../map/joystick';

/** 4-way facing from a delta (for the chop-swing direction). */
const dirTo = (dx: number, dy: number): Dir =>
  Math.abs(dx) > Math.abs(dy) ? (dx < 0 ? 'left' : 'right') : (dy < 0 ? 'up' : 'down');
import { createArenaDebug, type ArenaDebug } from '../arena/debug';
import { simulateMatch } from '../arena/sim';
import { arenaBrApi } from '../game-api';
import { showResultScreens } from '../arena/result-screens';
import { deriveProfile, type BotProfile } from '../arena/profile';
import { decide, newBotMemory, type AiWorld, type BotMemory } from '../arena/bot-ai';
import { buildEnemyViews, updateFacing, type SenseEnemy } from '../arena/bot-sense';
import { ARENA, killGold } from '../arena/combat';
import { rollTier, type Personality, type SkillTier } from '../arena/names';
import { compareSurvivors, type HeroResult } from '../arena/scoring';
import { initAudio, playSfx, playSfxAt, setAudioOverlay, setListener } from '../audio/audio';
import { VICTORY_STING_MS } from '../audio/track';
import { soundControlRows } from '../audio/audio-controls';
import { startArenaTour, arenaTutorialSeen } from '../tutorial/arena-tour';
import type { CoachHandle } from '../tutorial/coach';

/**
 * The arena match — client-side, deterministic from a seed. This is the "make it
 * appear" layer: it generates the lava-and-mushroom map, runs the match timeline
 * (lineup → countdown → the flicker appearance → prep → battle) and renders every
 * hero with a nickname plate, HP bar and a live HUD. Bot AI and combat resolution
 * (11.4–11.5) slot into the BATTLE phase's per-tick hook; for now heroes take up
 * their positions and the timeline drives the phases so the whole flow is visible.
 */

type Phase = 'lineup' | 'countdown' | 'appear' | 'prep' | 'battle' | 'end';
const PHASE_MS: Record<Phase, number> = {
  // lineup is longer so the §6.8 "opponents connecting" fill completes before countdown.
  // battle is 5 minutes (owner).
  // battle 210s; the safe zone stays open until 60s remain (ARENA.SAFE_CLOSE_LAST_MS),
  // so the last minute is always an open fight with nowhere to hide.
  // Snappy intro so the fight starts fast (was ~35s of lineup+prep, which read as
  // "just a loading screen, no battle"). Now battle begins ~9s in.
  lineup: 3_500, countdown: 3_000, appear: 1_000, prep: 2_500, battle: 210_000, end: 0,
};
const ROSTER_FILL_WINDOW_MS = 2_800;   // all 24 bots connect within this window
const PREP_LOCK_MS = 1_200;   // first stretch of prep: study the map before positioning
const HERO_COUNT = 25;
const TOWER_CLEAR_TILES = 4;   // spawn ring inner radius — outside the 5-wide unwalkable tower
const AI_TICK_MS = 200;        // 5 Hz decisions
const PURSUE_STALL_MS = 600;   // held outside range this long → stop holding, close in

/**
 * The player's own swing cadence in the arena.
 *
 * Bots swing on their profile's `cooldownMs`, and the player was given the same
 * number — around a second — with combat resolving automatically whenever
 * something wandered into range. Owner: "allow in the fight attack by click, but
 * unlimited times, for example you clicked attack, after only 0.3 0.5sek next
 * attack." So the player gets a fixed fast cadence of their own, and a click on
 * an enemy now MEANS something: it picks the target instead of leaving the choice
 * to whoever happens to be nearest.
 *
 * Deliberately faster than any bot. The player is one fighter against
 * twenty-four; the swing rate is the compensation, and it is capped at 400ms so a
 * click-spammer gains nothing over someone holding position — the cooldown, not
 * the input rate, is what decides how often damage lands.
 */
const PLAYER_ATTACK_CD_MS = 400;
const PERSONALITIES: Personality[] = ['aggressor', 'farmer', 'camper', 'tactician', 'coward'];

/** Live per-hero combat state (mirrors the sim, but drives the rendered heroes). */
interface CombatState {
  profile: BotProfile;
  /** Crafted gear — the player's real tiers, a bot's rolled loadout. */
  gear: ArenaGear;
  kills: number; pendingGold: number; goldBanked: number; streak: number;
  safeCd: number; attackCd: number; aiCd: number; campMs: number;
  /**
   * Perception memory, target commitment, facing and decision stagger — see
   * arena/bot-ai.ts. Replaces the old `firstSeen` map, which only ever delayed the
   * FIRST sighting and then granted permanent map-wide awareness of everyone.
   */
  mem: BotMemory;
  /** Previous tile position: the facing heading, and the "was it moving?" aim penalty. */
  px: number; py: number; moving: boolean;
  wasInSafe: boolean;
  deathOrder: number; timeAlive: number; entryOrder: number;
  attackTargetId: string | null;
  // §6.6 smooth movement: current path goal + stuck/wander bookkeeping so bots don't
  // replan every tick (twitch), don't re-wander on a momentary clamp, and recover
  // when a prop pins them (unstuck).
  goalTx: number; goalTy: number; hasGoal: boolean; stuckMs: number; wanderCd: number;
  /** How long pursue() has been holding just outside strike range without closing. */
  stallMs: number;
  rng: () => number;
}

/**
 * Dress an appearance in crafted plate (mirrors the farm's gearedLook).
 *
 * Used for BOTH the player — from his real crafted tiers — and the bots, whose
 * tiers are rolled. Tier 0 leaves that slot bare.
 */
function withArmor(base: Appearance, helmet: number, chest: number, legs: number): Appearance {
  const g: Appearance = { ...base };
  // armourLayerFile (ui/armor-art.ts) is the one table for the whole game — farm
  // hero, bag icon, anvil icon and arena fighter. The arena must not dress the
  // same player in a different metal, or a different helm SET, from the one his
  // farm shows.
  //
  // The paint goes through the same shared answer (map/ores armourPaintColor) as
  // the farm's hero. Leaving it out here would have re-created the very mismatch
  // this function's comment exists to prevent — a fighter in mythril cyan whose
  // farm hero is in emerald green.
  const h = armourLayerFile('helmet', helmet);
  if (h) { g.headgear = { file: h, color: armourPaintColor(helmet) }; g.hair = { file: null, color: null }; }
  const c = armourLayerFile('chest', chest);
  if (c) g.shirt = { file: c, color: armourPaintColor(chest) };
  const l = armourLayerFile('legs', legs);
  if (l) g.pants = { file: l, color: armourPaintColor(legs) };
  return g;
}

// Arena runs its own music bed; the farm restores day/night on return.
export function renderArenaMatch(
  root: HTMLElement, ctx: GameCtx, seed: number, onFinish?: () => void,
  /**
   * The field as the server's lobby seated it — real players first, bots in the
   * slots nobody claimed. Omitted (or unreachable) means a solo field: you plus
   * bots, which is the old behaviour and still the common case at 3am.
   */
  seats?: readonly MatchSeat[],
  /**
   * Refresh survival (defect 19, rule R3).
   *
   * `paid` is the entry the server already charged, carried so a RESTORED match can
   * print a truthful forfeit warning without asking (and re-charging) the server.
   * `matchId` is the ArenaBrMatch row that entry opened: EVERY result posted from
   * here — the finish and the teardown forfeit alike — is claimed against it, and a
   * result without it is refused (arena-br.service.ts). It is carried rather than
   * re-fetched because asking the server again would mean entering again.
   * `restore` is a snapshot of a match this same tab was fighting before a reload:
   * the field, the roster, every appearance and every AI profile are still rebuilt
   * from the seed exactly as on a fresh match — the snapshot only overwrites the
   * mutable half (positions, HP, gold, kills, clocks). See arena/resume.ts.
   */
  opts?: { paid?: number; matchId?: string; restore?: MatchSnapshot | null },
): ViewTeardown {
  // Battle bed for the duration of the match. Declared as an OVERLAY rather than
  // played directly: clearing it on teardown is what stops the arena bleeding into
  // the farm afterwards ("after entering from arena to game, there is still
  // background sound from arena"). The arena bed is also mixed at half level —
  // see TRACK_GAIN in audio/track.ts — so it sits under the fighting.
  initAudio();
  setAudioOverlay('arena-match');
  clear(root);
  let alive = true;
  const restore = opts?.restore ?? null;
  const paidEntry = opts?.paid ?? 0;
  /** Prefer the restored id: on a resume the caller passes the snapshot's own. */
  const matchId = opts?.matchId || opts?.restore?.matchId || '';
  /** ms the wall clock moved between the snapshot and this mount (see catchUpMs). */
  const restoreGap = restore ? catchUpMs(restore, Date.now()) : 0;
  let closeResults: (() => void) | null = null;   // live results overlay, torn down with the match
  // One battle length for everyone. The 40-minute "test mode" branch went with the
  // Test Arena entry point it existed for — a second, longer match shape that no
  // player could ever reach was only a way for the two to drift apart.
  const phaseDur = (p: Phase) => PHASE_MS[p];

  const wrap = el('div', 'relative w-full h-full overflow-hidden bg-black');
  const stageHost = el('div', 'absolute inset-0 select-none', { style: 'touch-action:none' });
  // HUD chrome
  const topBar = el('div', 'absolute top-3 left-1/2 -translate-x-1/2 text-center pointer-events-none');
  const phaseLabel = el('div', 'arena-font text-[15px] tracking-wide text-[#ffcf8a] drop-shadow-[0_2px_0_rgba(90,20,10,0.9)]');
  const bigTimer = el('div', 'arena-font text-6xl text-white drop-shadow-[0_3px_0_rgba(122,31,10,0.9)]');
  topBar.append(phaseLabel, bigTimer);
  const liveCounter = el('div', 'arena-font absolute top-3 right-3 px-3 py-1.5 rounded-lg bg-black/55 border border-[#7a3a12] text-[#ffcf8a] text-sm', {}, '25 alive');
  const goldVal = el('span', '', {}, '0 pending');
  const goldHud = el('div', 'arena-font absolute bottom-3 left-3 px-3 py-1.5 rounded-lg bg-black/55 border border-[#7a3a12] text-gold text-sm flex items-center gap-1.5', {}, aicon(AICON.coin, 15), goldVal);
  const rosterPanel = el('div', 'absolute inset-0 flex items-center justify-center bg-black/70');
  // Controls hint, shown during prep + battle (hidden otherwise).
  const controlsHint = el('div', 'arena-font absolute bottom-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-lg bg-black/55 border border-[#7a3a12] text-[#e9d6bd] text-xs pointer-events-none hidden', {}, 'WASD or click to move');
  // Collision debug: the tile the collision checks under the player and whether it's
  // blocked/lava. HIDDEN by default — it is a developer readout, and it used to be
  // painted over every real player's battle. It rides the walkability overlay (B),
  // which is the toggle it exists to explain.
  const dbg = el('div', 'arena-font absolute bottom-12 left-3 px-2 py-1 rounded bg-black/70 text-[#9fe] text-[11px] pointer-events-none hidden', {}, '');
  // ─── In-fight sound controls ──────────────────────────────────────────────
  // The match is fullscreen and the shell's Settings gear is not reachable from
  // it, so the arena carries its own music/effects controls (owner). Same rows as
  // the settings modal — one audio store, no second copy of the state.
  const soundPanel = el('div',
    'arena-font absolute top-14 right-3 z-20 hidden w-[260px] max-w-[80vw] px-3 py-2 rounded-lg bg-black/85 border border-[#7a3a12] text-[#e9d6bd]');
  soundPanel.append(...soundControlRows());
  const soundBtn = el('button',
    'arena-font absolute top-3 right-[110px] z-20 px-2.5 py-1.5 rounded-lg bg-black/55 border border-[#7a3a12] text-[#ffcf8a] text-sm',
    {
      title: 'Sound',
      onClick: () => soundPanel.classList.toggle('hidden'),
      // The pack has no speaker sprite, and an emoji speaker was the one system
      // glyph on the arena HUD. The word says the same thing in the arena font.
    }, 'Sound');
  wrap.append(stageHost, topBar, liveCounter, goldHud, controlsHint, dbg, soundBtn, soundPanel, rosterPanel);
  root.append(wrap);

  // ─── Match data ───────────────────────────────────────────────────────────
  // The grass battlefield + its nav are built async in the boot below.
  let field!: ArenaField;
  let fnav!: FieldNav;
  let navService!: NavService;
  const lavaTiles = new Set<number>();   // tile indices that are lava (for the debug readout)
  let worldC: Container | null = null;   // the pannable world (ground + heroes)
  const youName = ctx.user.username || 'You';
  // Fighters are seated BY SLOT, so a lobby with more humans in it does not change
  // which bot sits in any slot a human did not take (see buildFighters).
  const fighters: Fighter[] = buildFighters(seed, seats?.length ? seats : soloSeats(youName, HERO_COUNT), youName, ctx.user.id);
  const humansInField = fighters.filter((f) => f.isHuman).length;
  let app: Application | null = null;
  const heroes: ArenaHero[] = [];
  const requesting = new Set<string>();                   // agents awaiting a path
  const combat = new Map<string, CombatState>();          // live AI + combat state per hero
  let deaths = 0, battleClock = 0;
  const inSafeZone = (x: number, y: number) => Math.hypot(x - field.safe.cx, y - field.safe.cy) <= field.safe.r;
  const heldKeys = new Set<string>();                     // WASD for the player
  let joyVec: { x: number; y: number } | null = null;     // touch joystick vector
  let joystick: Joystick | null = null;
  const wanderRng = mulberry32(seed ^ 0x9e3779b1);
  const heldDir = (): { x: number; y: number } | null => {
    if (joyVec) return joyVec;                             // touch joystick wins when engaged
    let x = 0, y = 0;
    if (heldKeys.has('w') || heldKeys.has('ArrowUp')) y -= 1;
    if (heldKeys.has('s') || heldKeys.has('ArrowDown')) y += 1;
    if (heldKeys.has('a') || heldKeys.has('ArrowLeft')) x -= 1;
    if (heldKeys.has('d') || heldKeys.has('ArrowRight')) x += 1;
    return x || y ? { x, y } : null;
  };
  let debug: ArenaDebug | null = null;
  let gridDbg: Graphics | null = null;   // collision-vs-render overlay (toggle B)
  let player: ArenaHero | null = null;
  let camTarget: ArenaHero | null = null;   // who the camera tracks (player, or a spectated hero)
  let freeCam = false;                       // true after you drag the map — camera stops auto-following
  /** The enemy the player last clicked, held until it dies or they click the ground. */
  let clickTargetId: string | null = null;
  let freezeTimer = false;                   // debug (K): pause the battle countdown to roam the map
  let safeToastCd = 0;                       // throttle for the "target is in the sanctuary" explainer
  // Background-safe ticker: rAF is paused for a hidden tab, which would freeze the
  // whole match. When hidden we drive the sim from a timer instead.
  let bgTimer = 0;
  let lastHiddenTs = 0;
  const onVisibility = () => {
    if (document.hidden) { lastHiddenTs = performance.now(); saveSnapshot(); }
  };
  // The reliable last moment before a reload. `beforeunload` is not fired on mobile
  // Safari at all and `unload` is deprecated; `pagehide` is the one both desktop and
  // iOS actually deliver, so it is where the final snapshot is taken.
  const onPageHide = () => saveSnapshot();

  /** First-match guided tour (tutorial/arena-tour.ts); null once it has run. */
  let tour: CoachHandle | null = null;

  // A restored match skips the lineup/countdown theatre: those beats already happened.
  // 'appear' is the phase that WAITS for heroesReady, so parking there keeps the
  // restored clocks from running before there is anyone to apply them to — the real
  // phase is set at the end of placeHeroes, once the fighters exist.
  let phase: Phase = restore ? 'appear' : 'lineup';
  /** True once the result for this match has been sent — a forfeit must not double it. */
  let resultSent = false;

  /**
   * Arm or clear the "you will forfeit" prompt as the fight starts and ends.
   *
   * Called on every phase change rather than once, because the answer changes:
   * during prep/battle leaving costs the match, and on the results screen it
   * costs nothing. Idempotent, so calling it every frame is fine.
   */
  function syncLeaveGuard() {
    const live = (phase === 'prep' || phase === 'battle') && !!player?.alive && !resultSent;
    if (live) {
      setLeaveGuard('You are in a live arena match. Leaving now forfeits it — you will be scored last and lose rating.');
    } else clearLeaveGuard();
  }
  let phaseT = 0;

  // Hero positions are world pixels (TILE=48); nav works in tiles. ONE convention
  // bridges them — arena/coords.ts — and everything here goes through it: spawning,
  // path waypoints, strike range, the safe-zone test and the AI's world view. They
  // used to disagree by most of a tile in Y (the sprite anchor vs the feet), which is
  // why heroes walked their routes one row high and jammed on the border and the
  // lava seams.
  const tileOf = (h: ArenaHero) => tileOfPx(h.x, h.y, ARENA_ANCHOR);
  /**
   * Keep a hero inside the walkable interior.
   *
   * The margin is derived from the SAME constant computeArenaBlocked uses, because
   * the two had drifted: a 1.5-tile clamp against a 2-tile blocked band shoved heroes
   * into a wall cell, and the mover's collision (map/player.ts) responds to "every
   * candidate is solid" by dropping the path and freezing. That was the edge sticking.
   * +0.5 puts the feet on the centre of the first walkable tile, never its boundary.
   */
  const CLAMP_MARGIN = ARENA_BORDER_BAND + 0.5;
  const clampPos = (x: number, y: number): [number, number] => {
    const lo = pxOfTile(CLAMP_MARGIN, CLAMP_MARGIN, ARENA_ANCHOR);
    const hi = pxOfTile(field.cols - CLAMP_MARGIN, field.rows - CLAMP_MARGIN, ARENA_ANCHOR);
    return [Math.max(lo.x, Math.min(hi.x, x)), Math.max(lo.y, Math.min(hi.y, y))];
  };
  const blockedPx = (wx: number, wy: number): boolean => {
    const tx = Math.floor(wx / TILE), ty = Math.floor(wy / TILE);
    if (tx < 0 || ty < 0 || tx >= field.cols || ty >= field.rows) return true;
    return fnav.grid.walkable[tileIndex(tx, ty, field.cols)] === 0;
  };

  // Path a hero to a tile goal — the farm handle walks the smoothed route (in px).
  // Nearest walkable tile to (gx,gy), spiralling out — so a click on lava / off the
  // platform snaps to the closest ground instead of doing nothing (the arena's
  // walkable platform is smaller than the lava-tinted floor looks).
  function nearestWalkable(gx: number, gy: number): { x: number; y: number } | null {
    const ok = (x: number, y: number) =>
      x >= 0 && y >= 0 && x < field.cols && y < field.rows && !!fnav.grid.walkable[tileIndex(x, y, field.cols)];
    if (ok(gx, gy)) return { x: gx, y: gy };
    for (let r = 1; r <= 12; r++) {
      for (let dy = -r; dy <= r; dy++) for (let dx = -r; dx <= r; dx++) {
        if (Math.max(Math.abs(dx), Math.abs(dy)) !== r) continue;   // ring perimeter only
        if (ok(gx + dx, gy + dy)) return { x: gx + dx, y: gy + dy };
      }
    }
    return null;
  }

  // Path a hero to a tile goal — the farm handle walks the smoothed route (in px).
  function goTo(h: ArenaHero, gxTile: number, gyTile: number) {
    if (requesting.has(h.id)) return;
    const cx = Math.max(0, Math.min(field.cols - 1, Math.floor(gxTile))), cy = Math.max(0, Math.min(field.rows - 1, Math.floor(gyTile)));
    const snapped = nearestWalkable(cx, cy);
    if (!snapped) return;
    const gx = snapped.x, gy = snapped.y;
    const { tx, ty } = tileOf(h);
    const c = combat.get(h.id);
    if (c) { c.goalTx = gx; c.goalTy = gy; c.hasGoal = true; }
    requesting.add(h.id);
    navService.requestPath(h.id, Math.floor(tx), Math.floor(ty), gx, gy, { priority: 1, sigma: combat.get(h.id)?.profile.pathNoiseSigma ?? 0.2, rng: wanderRng })
      .then((r) => { requesting.delete(h.id); if (!r.superseded && r.path.length > 1) h.setPathPx(r.path.map((p) => pxOfTile(p.x, p.y, ARENA_ANCHOR))); })
      .catch(() => requesting.delete(h.id));
  }

  // §6.6: only replan when the goal actually moved (>1.5 tiles) or the bot has no
  // live path — feeding a fresh (randomly-smoothed) route every 200ms AI tick is what
  // made bots twitch. `moving` is the proxy for "still walking a path".
  function goToIfNeeded(h: ArenaHero, c: CombatState, gxTile: number, gyTile: number) {
    const changed = !c.hasGoal || Math.abs(gxTile - c.goalTx) > 1.5 || Math.abs(gyTile - c.goalTy) > 1.5;
    if (changed || !h.handle.moving) goTo(h, gxTile, gyTile);
  }

  // Pursue a target with anti-jitter: in strike range → hold and swing; a hair
  // outside while already stopped → HOLD (don't twitch toward a target that keeps
  // stepping in/out of range); close → steer STRAIGHT at it (no pathfinding, so no
  // side-to-side zig-zag); far → route around obstacles.
  function pursue(h: ArenaHero, c: CombatState, gxTile: number, gyTile: number, range: number) {
    const { tx, ty } = tileOf(h);
    const dx = gxTile - tx, dy = gyTile - ty, d = Math.hypot(dx, dy);
    if (d <= range) { h.stop(); c.hasGoal = false; return; }
    // Hysteresis deadband — but only for a short while. A stopped bot a hair outside
    // range used to hold here FOREVER: it wasn't moving, so it never re-planned, so it
    // never closed the last third of a tile. Twenty-five of those standing around is
    // "the heroes barely fight". After STALL_MS of not closing, it commits.
    if (d <= range * 1.3 && !h.handle.moving) {
      c.stallMs += AI_TICK_MS;
      if (c.stallMs < PURSUE_STALL_MS) return;
    }
    c.stallMs = 0;
    if (d <= 5) { const l = d || 1; h.setKeys(dx / l, dy / l); c.hasGoal = false; return; }
    goToIfNeeded(h, c, gxTile, gyTile);
  }

  function requestWander(h: ArenaHero, inSafeOnly: boolean) {
    const { tx: htx, ty: hty } = tileOf(h);
    for (let tries = 0; tries < 30; tries++) {
      let tx: number, ty: number;
      if (inSafeOnly) {
        const a = wanderRng() * Math.PI * 2, rr = wanderRng() * (field.safe.r - 1);
        tx = Math.floor(field.safe.cx + Math.cos(a) * rr); ty = Math.floor(field.safe.cy + Math.sin(a) * rr);
      } else {
        const a = wanderRng() * Math.PI * 2, rr = 6 + wanderRng() * 18;
        tx = Math.floor(htx + Math.cos(a) * rr); ty = Math.floor(hty + Math.sin(a) * rr);
      }
      if (tx < 0 || ty < 0 || tx >= field.cols || ty >= field.rows) continue;
      // Anti-jitter: never re-pick essentially the current tile — a sub-2-tile hop lets
      // a bot arrive and instantly re-wander, which reads as a twitch.
      if (Math.max(Math.abs(tx - Math.floor(htx)), Math.abs(ty - Math.floor(hty))) < 2) continue;
      if (fnav.grid.walkable[tileIndex(tx, ty, field.cols)]) { goTo(h, tx, ty); return; }
    }
  }

  /**
   * Agent-vs-agent separation (Movement spec §3.2, nav/avoidance.ts).
   *
   * A* handles static geometry and deliberately does NOT know about other agents, so
   * the separation layer has to be driven by the caller — and nothing ever called it.
   * Twenty-five heroes therefore walked straight through each other and piled into a
   * single occupied tile, which is the "collisions are clunky" half of the complaint.
   *
   * The push is applied as a small POSITIONAL nudge rather than a velocity, because the
   * movement here is the farm's PlayerHandle and we don't own its integrator. Per
   * avoidance.ts's own contract the caller must clamp the push against the static grid,
   * so each axis is committed only if the foot cell it lands in is walkable — avoidance
   * can never shove a fighter into lava or through the wall.
   */
  const MAX_SEPARATION_PX_PER_SEC = TILE * 1.2;
  function applySeparation(dtSec: number) {
    const agents: AvoidAgent[] = [];
    for (const h of heroes) {
      if (!h.alive) continue;
      const t = tileOf(h);
      agents.push({ id: h.id, x: t.tx, y: t.ty, vx: 0, vy: 0, hp: h.hp });
    }
    if (agents.length < 2) return;
    const out = computeAvoidance(agents);
    for (const h of heroes) {
      if (!h.alive) continue;
      const o = out.get(h.id);
      if (!o || (!o.pushX && !o.pushY)) continue;
      // computeAvoidance returns an ACCELERATION (tiles/sec² up to SEP_FORCE), and we
      // have no velocity to integrate it into — so read its magnitude as a severity in
      // 0..1 and spend that fraction of the per-frame nudge budget. Multiplying the
      // acceleration by dt as if it were a velocity saturated the cap for every
      // overlapping pair and flattened avoidance.ts's quadratic ramp into a constant
      // shove; this keeps the ramp: gentle at the edge of personal space, hard on contact.
      const cap = MAX_SEPARATION_PX_PER_SEC * dtSec;
      const mag = Math.hypot(o.pushX, o.pushY);
      const severity = Math.min(1, mag / SEP_FORCE);
      const dx = (o.pushX / mag) * severity * cap, dy = (o.pushY / mag) * severity * cap;
      // Foot-cell test per axis: blockedPx is fed the collision Y the mover uses.
      const nx = blockedPx(h.x + dx, h.y - FOOT_OFFSET) ? h.x : h.x + dx;
      const ny = blockedPx(nx, h.y + dy - FOOT_OFFSET) ? h.y : h.y + dy;
      if (nx !== h.x || ny !== h.y) { h.handle.setPos(nx, ny); h.syncOverlay(); }
    }
  }

  // Advance every alive hero with the farm's own animate+move+collide update.
  function applyMovement(dtSec: number) {
    navService.tick(2);
    for (const h of heroes) {
      if (!h.alive) continue;
      const px0 = h.x, py0 = h.y;
      h.update(dtSec, clampPos, blockedPx);
      h.syncOverlay();
      // §6.6 unstuck: an agent with a goal that isn't actually moving (pinned on a prop,
      // path abandoned by the handle's axis-slide) gets nudged to a fresh nearby tile
      // after ~0.8s instead of standing frozen. The PLAYER used to be excluded, which
      // is why a player who walked into a seam simply stopped responding — their path
      // was gone and nothing ever gave them a new one. They now get the same ladder,
      // minus the re-wander: a player is steering, so we only clear the dead path and
      // let their next input or click take effect.
      const c = combat.get(h.id);
      if (c && c.hasGoal) {
        if (Math.hypot(h.x - px0, h.y - py0) < 0.3) c.stuckMs += dtSec * 1000; else c.stuckMs = 0;
        if (c.stuckMs > 800) {
          c.stuckMs = 0; c.hasGoal = false; requesting.delete(h.id);
          if (h.isPlayer) h.stop(); else requestWander(h, false);
        }
      }
    }
    applySeparation(dtSec);
    // Advance + retire death puffs (expand + fade over ~0.5s).
    for (let i = puffs.length - 1; i >= 0; i--) {
      const p = puffs[i]; p.t += dtSec;
      const k = p.t / 0.5;
      if (k >= 1) { p.g.destroy(); puffs.splice(i, 1); continue; }
      p.g.clear().circle(0, -TILE * 0.4, TILE * (0.2 + k * 0.5)).stroke({ color: 0xffd0a0, width: 3, alpha: 1 - k });
    }
    if (debug) debug.redraw([]);
  }

  // Prep milling: wander (in the safe space while locked), then move.
  // §6.6: a per-bot wander cooldown stops the stop/re-plan stutter — a bot that
  // momentarily clamps against a neighbour won't instantly re-path every frame.
  function wanderStep(dtSec: number, inSafeOnly: boolean) {
    for (const h of heroes) {
      if (!h.alive || h.isPlayer) continue;   // the player drives themselves during prep (see below)
      const c = combat.get(h.id);
      if (c) c.wanderCd -= dtSec * 1000;
      if (!h.handle.moving && !requesting.has(h.id) && (!c || c.wanderCd <= 0)) {
        requestWander(h, inSafeOnly);
        if (c) c.wanderCd = 600 + Math.floor(c.rng() * 600);
      }
    }
    // PREP is the "gather in the safe zone" window: the player walks under their own
    // control (keyboard / click-to-move / joystick), damage still off — but stays
    // INSIDE the safe zone. When BATTLE starts they're released onto the whole map.
    if (player) {
      const dir = heldDir();
      if (dir) player.setKeys(dir.x, dir.y);   // key-release stop handled by onKeyUp
    }
    applyMovement(dtSec);
    // Contain everyone in the safe zone during prep: pull any hero that strays past
    // the ring edge back onto it (players and bots alike).
    const centre = pxOfTile(field.safe.cx + 0.5, field.safe.cy + 0.5, ARENA_ANCHOR);
    const cxp = centre.x, cyp = centre.y, maxR = (field.safe.r - 0.6) * TILE;
    for (const h of heroes) {
      if (!h.alive) continue;
      const dx = h.x - cxp, dy = h.y - cyp, d = Math.hypot(dx, dy);
      if (d > maxR) { h.handle.setPos(cxp + (dx / d) * maxR, cyp + (dy / d) * maxR); h.syncOverlay(); }
    }
  }

  // §6.7: a quick death puff where a hero falls, then the hero despawns (hero.kill).
  const puffs: { g: Graphics; t: number; x: number; y: number }[] = [];
  function deathPuff(x: number, y: number) {
    if (!field) return;
    const g = new Graphics();
    g.x = x; g.y = y; g.zIndex = 1e7;
    field.heroLayer.addChild(g);
    puffs.push({ g, t: 0, x, y });
  }

  function killHero(victim: ArenaHero, killer: ArenaHero | null) {
    if (!victim.alive) return;
    const vc = combat.get(victim.id)!;
    // The player's own death is a front-and-centre event; a bot's is a thing that
    // happened over there. `arena_death` is the dedicated cue when it exists and
    // silently falls back to the ordinary mob death until then.
    if (victim.isPlayer) playSfx('player_death');
    else playSfxAt('arena_death', victim.x, victim.y, { fallback: 'mob_death' });
    deathPuff(victim.x, victim.y);
    victim.kill();
    vc.deathOrder = ++deaths;
    vc.pendingGold = 0;   // lost on death (banked stays)
    if (killer) {
      const kc = combat.get(killer.id)!;
      kc.pendingGold += killGold(kc.streak);
      kc.streak += 1; kc.kills += 1;
    }
    // If the PLAYER just died, start spectating their killer (or nearest survivor) so
    // the camera keeps showing the action — the viewer can click any fighter to switch.
    if (victim.isPlayer) {
      let next = killer && killer.alive ? killer : null;
      if (!next) {
        let bd = Infinity;
        for (const h of heroes) { if (h.alive && !h.isPlayer) { const d = Math.hypot(h.x - victim.x, h.y - victim.y); if (d < bd) { bd = d; next = h; } } }
      }
      if (next) { camTarget = next; freeCam = false; }
      // YOUR MATCH IS OVER THE MOMENT YOU DIE.
      //
      // showResults() only ever ran from setPhase('end'), which is the timer expiring or
      // one hero left — so a player killed at 0:20 sat through three more minutes with no
      // result and no way out that wasn't scored as a forfeit. The result is fully known
      // here (buildLiveResults reads the same live state), so report it now: the screens
      // carry the place, kills and banked gold, and their Done button is the exit.
      // resultSent/resultsShown keep the later setPhase('end') and the teardown forfeit
      // (which is gated on player?.alive) from double-submitting.
      //
      // The result window covers the map, so spectating cannot happen AT THE SAME TIME
      // as reading it: the screens win, and "Keep watching" is the explicit way back to
      // the camera we just parked on `next`. The overlay returns at the end of the
      // match (resultsShown is cleared) so choosing to watch never costs the results.
      showResults(next ? () => {
        resultsShown = false; closeResults = null;
        toast(`Spectating ${next!.nickname}. Drag to look around, click a fighter to follow.`);
      } : undefined);
    }
  }

  // The live battle: AI decisions (5 Hz) → movement + melee + gold/banking + hazards.
  // This is what makes the rendered match the real one — same decide()/combat rules
  // the sim uses, driving the on-screen heroes.
  function battleStep(dtSec: number, battleRemainMs: number) {
    battleClock += dtSec * 1000;
    // Mix around whoever the camera is on — the player while they live, the hero
    // they spectate afterwards. Without this every scrap on the map is equally loud.
    const ear = camTarget && camTarget.alive ? camTarget : player;
    if (ear) setListener(ear.x, ear.y);
    // The safe zone CLOSES ARENA.SAFE_CLOSE_LAST_MS (60s) before the end — after that
    // it's just grass. The comment here used to say "3 minutes", which matched nothing:
    // the constant is 60_000 (combat.ts) and the owner's note there says one minute, so
    // the CODE is right and the comment was the bug. On a 210s battle the sanctuary is
    // therefore live for the first 150 seconds.
    const safeActive = battleRemainMs > ARENA.SAFE_CLOSE_LAST_MS;
    safeToastCd = Math.max(0, safeToastCd - dtSec * 1000);

    for (const h of heroes) {
      if (!h.alive) continue;
      const c = combat.get(h.id)!;
      const { tx, ty } = tileOf(h);            // hero position in tiles
      c.timeAlive += dtSec * 1000;
      c.safeCd = Math.max(0, c.safeCd - dtSec * 1000);
      c.attackCd = Math.max(0, c.attackCd - dtSec * 1000);
      c.aiCd -= dtSec * 1000;
      let inSafe = safeActive && inSafeZone(tx, ty);
      if (inSafe) {
        c.campMs += dtSec * 1000;
        if (c.campMs > ARENA.SAFE_CAMP_MAX_MS) {
          // Camped >30s → walk them back out to the perimeter, no more protection.
          const dx = tx - field.safe.cx, dy = ty - field.safe.cy, l = Math.hypot(dx, dy) || 1;
          goTo(h, field.safe.cx + (dx / l) * (field.safe.r + 3), field.safe.cy + (dy / l) * (field.safe.r + 3));
          c.campMs = 0; inSafe = false;
          if (h.isPlayer) toast('Kicked out — no camping the safe zone!');
        } else {
          // NOTHING HEALS IN HERE (owner, 2026-09-02). The sanctuary is the bank and
          // the shield: bank pending gold on entry, and stay untouchable while it is
          // open. Nobody is locked in — free entry, free exit, no HP reward, which is
          // what stops the whole roster orbiting the middle of the map.
          if (!c.wasInSafe && c.pendingGold > 0) { c.goldBanked += Math.floor(c.pendingGold); c.pendingGold = 0; c.streak = 0; }
        }
      } else c.campMs = 0;
      c.wasInSafe = inSafe;

      // The player is input-driven (WASD/click); only the 24 bots run the AI.
      // WASD sets keys (overrides a click-target); no keys → the click-target/idle
      // plays out. Key-release stop is handled by onKeyUp, so we never zero here
      // (zeroing every idle frame is what fought click-to-move).
      if (h.isPlayer) {
        const dir = heldDir();
        if (dir) h.setKeys(dir.x, dir.y);      // farm handle: animated walk in that direction
        // Who the player is swinging at. A CLICKED enemy wins while it is alive and
        // in range — picking a target and having the game hit someone else instead
        // is the opposite of a control. Otherwise fall back to nearest-in-range, so
        // standing in a melee still works without clicking.
        // In range but standing on the sanctuary stone → protected, not broken. The
        // rule always existed; nothing on screen ever said so, which is why "some
        // players are unattackable" read as a bug rather than as a sanctuary. Say it
        // out loud, throttled, when the player is actually swinging at one.
        const inRange = (e: ArenaHero) => {
          const et = tileOf(e);
          return Math.hypot(et.tx - tx, et.ty - ty) <= c.profile.range;
        };
        const protectedNow = (e: ArenaHero) => {
          const et = tileOf(e);
          return safeActive && inSafeZone(et.tx, et.ty);
        };
        const strikeable = (e: ArenaHero): boolean =>
          e !== h && e.alive &&
          canStrike({ from: { tx, ty }, to: tileOf(e), range: c.profile.range, attackerProtected: inSafe, targetProtected: protectedNow(e) });
        const clicked = clickTargetId ? heroes.find((e) => e.id === clickTargetId) : null;
        if (clicked && !clicked.alive) clickTargetId = null;   // it died; stop aiming at a corpse
        if (clicked && clicked.alive && strikeable(clicked)) {
          c.attackTargetId = clicked.id;
        } else {
          if (clicked && clicked.alive && inRange(clicked) && protectedNow(clicked) && safeToastCd <= 0) {
            safeToastCd = 6_000;
            toast(`${clicked.nickname} is in the sanctuary — no damage until the safe zone closes.`);
          }
          let near: ArenaHero | null = null, nd = Infinity;
          for (const e of heroes) {
            if (!strikeable(e)) continue;
            const et = tileOf(e); const d = Math.hypot(et.tx - tx, et.ty - ty);
            if (d < nd) { nd = d; near = e; }
          }
          c.attackTargetId = near ? near.id : null;
        }
      } else if (c.aiCd <= 0) {
        // Per-decision latency on top of the fixed tick, so the 24 bots never think
        // in lockstep and none of them reacts instantly.
        c.aiCd = AI_TICK_MS + Math.floor(c.rng() * c.profile.decisionJitterMs);
        // Perception, honestly. This block used to hand every bot EVERY alive hero on
        // the whole map — exact position, exact HP, exact unbanked gold, through walls
        // — which is the charter's cardinal sin. Cone + line of sight + latency + a 4 s
        // memory now, the same eyes the player has. The old defence for omniscience was
        // that blind bots stopped fighting; the actual fix for that is the last-known-
        // position search and the map-wide patrol in bot-ai.ts, not x-ray vision.
        const visible: SenseEnemy[] = [];
        for (const e of heroes) {
          if (e === h || !e.alive) continue;
          const et = tileOf(e); const ec = combat.get(e.id);
          visible.push({ id: e.id, x: et.tx, y: et.ty, hpPct: e.hp / e.maxHp, kills: ec?.kills ?? 0 });
        }
        c.mem.facing = updateFacing(c.mem.facing, tx - c.px, ty - c.py);
        c.px = tx; c.py = ty;
        const seen = buildEnemyViews({
          selfId: h.id, x: tx, y: ty, facing: c.mem.facing, nowMs: battleClock,
          perception: c.mem.perception, facts: c.mem.facts,
          blocks: (gx, gy) => gx < 0 || gy < 0 || gx >= field.cols || gy >= field.rows || fnav.grid.walkable[tileIndex(gx, gy, field.cols)] === 0,
        }, visible);
        const world: AiWorld = {
          self: { id: h.id, x: tx, y: ty, hp: h.hp, maxHp: h.maxHp, pendingGold: c.pendingGold, profile: c.profile, safeCooldownMs: c.safeCd, inSafe },
          enemies: seen, safeCenter: { x: field.safe.cx + 0.5, y: field.safe.cy + 0.5 },
          distToSafe: (x, y) => { const gx = Math.floor(x), gy = Math.floor(y); return gx < 0 || gy < 0 || gx >= field.cols || gy >= field.rows ? Infinity : fnav.distToSafe[tileIndex(gx, gy, field.cols)]; },
          // `armedHazard` is null and `danger` flat HERE only: the live field
          // (arena/field.ts) generates no geysers and no lava-damage tiles, unlike the
          // sim map (arena/map.ts), where both are now wired and bots dodge telegraphs.
          // When the field grows hazards, feed them in here — the decision layer is
          // already waiting for them.
          nearestChokepoint: nearestChokeTile, danger: () => 0, armedHazard: null, shrinking: !safeActive, ringLethal: false, attackRange: c.profile.range, aliveCount: heroes.filter((x) => x.alive).length,
          memory: c.mem, rng: c.rng,
          // Drives the patrol's roaming destination — see wanderTarget in bot-ai.ts.
          // `bounds` is the LIVE field (80x80), not the sim's 120x120: without it the
          // patrol aimed up to four tiles outside the wall and every idle bot ended up
          // parked on the border ring.
          nowMs: battleClock, bounds: { w: field.cols, h: field.rows },
        };
        const intent = decide(world);
        if (intent.kind === 'attack') {
          c.attackTargetId = intent.targetId;
          const tgt = heroes.find((x) => x.id === intent.targetId);
          const tt = tgt && tgt.alive ? tileOf(tgt) : null;
          if (tt) pursue(h, c, tt.tx, tt.ty, c.profile.range);
          else { h.stop(); c.hasGoal = false; }
        }
        else if (intent.kind === 'move' || intent.kind === 'dodge') { c.attackTargetId = null; goToIfNeeded(h, c, intent.x, intent.y); }
        else {
          // No target → ROAM instead of standing frozen (livelier, and it explores the
          // map to find fights). Throttled by wanderCd so it doesn't re-path every AI
          // tick (which caused the twitch/standing-still look).
          c.attackTargetId = null; c.hasGoal = false;
          c.wanderCd -= AI_TICK_MS;
          if (!h.handle.moving && !requesting.has(h.id) && c.wanderCd <= 0) {
            requestWander(h, false);
            c.wanderCd = 900 + Math.floor(c.rng() * 1100);
          }
        }
      }

      // Resolve a melee hit on cooldown when a target is in range. Bots always play the
      // blocking swing animation. The PLAYER swings ONLY while standing still (no move
      // input, not walking) — so a walking player is never frozen mid-swing, but a
      // player standing next to an enemy visibly attacks it. Any move input interrupts
      // the swing (see onTap/onKey → player.interrupt()). Damage applies either way.
      if (c.attackTargetId && c.attackCd <= 0 && !inSafe) {
        const target = heroes.find((x) => x.id === c.attackTargetId);
        const tt = target && tileOf(target);
        if (target && target.alive && tt && canStrike({
          from: { tx, ty }, to: tt, range: c.profile.range,
          attackerProtected: inSafe, targetProtected: safeActive && inSafeZone(tt.tx, tt.ty),
        })) {
          c.attackCd = h.isPlayer ? PLAYER_ATTACK_CD_MS : c.profile.cooldownMs;
          const playerStill = !heldDir() && !h.handle.moving;
          if (!h.isPlayer || playerStill) void h.handle.playAction?.('attack', dirTo(tt.tx - tx, tt.ty - ty)).catch(() => undefined);
          // 25 fighters swinging at 1–2 Hz is exactly the pile-up the owner heard.
          // ONE sound per swing, positional and hard-capped. Twenty-five fighters
          // trading blows is the worst case in the game for audio: a separate
          // "connected" cue on top of the swing doubled an already dense mix into
          // mush. The hit still reads through the damage number and the flash.
          playSfxAt('sword_swing', h.x, h.y, { cooldownMs: 90, maxConcurrent: 2 });
          if (rollHit(c.profile, {
            targetMoving: !!target.handle.moving, selfHpPct: h.hp / h.maxHp,
            dist: Math.hypot(tt.tx - tx, tt.ty - ty), range: c.profile.range,
          }, c.rng())) {
            // Gear counts: the swinger's weapon tier and the target's armour, both
            // capped in combat.ts so a set can tilt a fight without deciding it.
            const tc = combat.get(target.id);
            target.setHp(target.hp - gearedDamage(c.profile.damage, c.gear, tc?.gear ?? NO_GEAR));
            if (target.hp <= 0) killHero(target, h);
          }
        }
      }
    }

    applyMovement(dtSec);

    // HUD.
    const pc = player && combat.get(player.id);
    if (pc) goldVal.textContent = `${Math.floor(pc.pendingGold)} pending${pc.goldBanked ? ` · ${pc.goldBanked} banked` : ''}`;

    // Collision debug (only while the B overlay is on — see `dbg` above): the tile the
    // collision samples under the player vs where the sprite base sits, and what's there.
    if (player && field && !dbg.classList.contains('hidden')) {
      const colTy = player.y - FOOT_OFFSET;                 // where solidAt actually checks
      const cft = { x: Math.floor(player.x / ARENA_TILE), y: Math.floor(colTy / ARENA_TILE) };
      const baseT = { x: Math.floor(player.x / ARENA_TILE), y: Math.floor(player.y / ARENA_TILE) };
      const idx = cft.y * field.cols + cft.x;
      const blk = fnav?.grid.walkable[idx] === 0;
      const lava = lavaTiles.has(idx);
      dbg.textContent = `col(${cft.x},${cft.y}) ${blk ? 'BLOCKED' : 'walk'}${lava ? ' LAVA' : ''} · base(${baseT.x},${baseT.y}) · footOff=${Math.round(FOOT_OFFSET)}`;
    }
  }

  const nearestChokeTile = (x: number, y: number) => {
    let best: { x: number; y: number } | null = null, bd = Infinity;
    for (const cidx of fnav.chokepoints) {
      const cx = cidx % field.cols, cy = (cidx - (cidx % field.cols)) / field.cols;
      const d = Math.hypot(cx - x, cy - y);
      if (d < bd) { bd = d; best = { x: cx + 0.5, y: cy + 0.5 }; }
    }
    return best;
  };

  /**
   * Build the live result set (same shape the sim produces), ranked survivors-first.
   *
   * Survivors are ordered by compareSurvivors (arena/scoring.ts) — the Score formula
   * minus its own place term — because the match usually ends on the 210s timer with a
   * dozen fighters alive and somebody has to be 1st. Owner: rank them by Score and give
   * 1st to the highest. The sim's placement sort (arena/sim.ts) calls the same function,
   * so the live path and the fallback can no longer disagree about what 1st place means.
   */
  function buildLiveResults(): HeroResult[] {
    const standing = (h: ArenaHero) => {
      const c = combat.get(h.id)!;
      return { kills: c.kills, goldBanked: c.goldBanked, hpPct: h.hp / h.maxHp, timeAliveMs: c.timeAlive, entryOrder: c.entryOrder };
    };
    const ranked = [...heroes].sort((a, b) => {
      if (a.alive !== b.alive) return a.alive ? -1 : 1;
      if (a.alive) return compareSurvivors(standing(a), standing(b));
      return combat.get(b.id)!.deathOrder - combat.get(a.id)!.deathOrder;
    });
    return ranked.map((h, i) => {
      const c = combat.get(h.id)!;
      return { heroId: h.id, survivalPlace: i + 1, kills: c.kills, goldBanked: c.goldBanked, survived: h.alive, timeAliveMs: c.timeAlive, entryOrder: c.entryOrder };
    });
  }

  // Lineup roster panel (spec §6.8: 25 slots that fill one-by-one as opponents
  // "connect", staggered 0.2–0.6s apart, so it feels like real players joining).
  const rosterSlots: HTMLElement[] = [];
  const rosterRevealAt: number[] = [];      // game-time ms (into lineup) each slot fills
  let rosterHeading: HTMLElement | null = null;
  let rosterHeadTxt: HTMLElement | null = null;
  let rosterConnected = 0;
  function paintRoster() {
    clear(rosterPanel);
    rosterSlots.length = 0; rosterRevealAt.length = 0; rosterConnected = 0;
    // Human seats are ALREADY connected — they are the players the lobby gathered.
    // Only bot slots get the staggered "connecting…" fill.
    const names = fighters.map((f) => f.nickname);
    const card = el('div', 'bg-surface/95 border border-border rounded-xl p-5 w-[440px] max-w-[92vw] max-h-[80vh] overflow-y-auto');
    rosterHeadTxt = el('span', '', {}, 'finding opponents…');
    rosterHeading = el('h3', 'arena-font text-gold text-center mb-3 flex items-center justify-center gap-1.5', {}, aicon(AICON.swords, 16), rosterHeadTxt);
    card.append(rosterHeading);
    const grid = el('div', 'grid grid-cols-2 gap-1.5 text-sm');
    const bots = Math.max(1, names.length - humansInField);
    names.forEach((n, i) => {
      const f = fighters[i];
      const slot = el('div', `px-2 py-1 rounded flex items-center gap-1.5 truncate transition-opacity ${f.isPlayer ? 'bg-gold/20 text-gold' : 'bg-surface-2 text-muted'}`);
      if (f.isHuman) {
        // A real player — yours starred, the others marked as players so the field
        // visibly is not "you and 24 bots" when other people actually showed up.
        slot.className = `px-2 py-1 rounded flex items-center gap-1.5 truncate transition-opacity ${f.isPlayer ? 'bg-gold/20 text-gold' : 'bg-surface-2 text-fg'}`;
        slot.append(aicon(AICON.star, 13), el('span', 'truncate', {}, n));
        rosterRevealAt.push(0); rosterConnected++;
      } else {
        slot.style.opacity = '0.35';
        slot.append(el('span', 'opacity-60 text-[11px]', {}, '···'), el('span', 'truncate', {}, 'connecting…'));
        // Spread the bots across a bounded window (~0.3s apart) with a little
        // jitter, so they trickle in one-by-one AND all finish before countdown.
        rosterRevealAt.push(300 + Math.floor((i / bots) * ROSTER_FILL_WINDOW_MS) + Math.floor(wanderRng() * 150));
      }
      rosterSlots.push(slot);
      grid.append(slot);
    });
    card.append(grid);
    rosterPanel.append(card);
  }

  // Reveal any slots whose connect-time has passed (driven by the lineup clock so it
  // respects a throttled/background tab instead of wall-clock timers).
  function revealRoster(lineupMs: number) {
    for (let i = 0; i < rosterSlots.length; i++) {
      if (rosterRevealAt[i] >= 0 && lineupMs >= rosterRevealAt[i]) {
        rosterRevealAt[i] = -1;   // mark done
        const slot = rosterSlots[i];
        slot.style.opacity = '1';
        slot.className = 'px-2 py-1 rounded flex items-center gap-1.5 truncate transition-opacity bg-surface-2';
        clear(slot);
        slot.append(aicon(AICON.shroom, 13), el('span', 'truncate', {}, fighters[i].nickname));
        rosterConnected++;
      }
    }
    if (rosterHeadTxt) rosterHeadTxt.textContent = rosterConnected >= rosterSlots.length
      ? `${HERO_COUNT} enter — 1 leaves` : `${rosterConnected}/${HERO_COUNT} connected…`;
  }

  // Scatter heroes: everyone starts inside the safe space (prep lock), spread on a ring.
  // Each is a real composed character — the player's own skin, and varied bot skins
  // with some wearing crafted armour, so the roster reads as 25 different people.
  let heroesReady = false;
  // Filled by placeHeroes, read when the combat states are built just below it.
  let playerGear: ArenaGear = { ...NO_GEAR };
  const botGear = new Map<string, ArenaGear>();
  async function placeHeroes() {
    if (!field || !worldC) return;
    const { cx, cy, r } = field.safe;
    // THE PLAYER FIGHTS IN THE ARMOUR HE CRAFTED.
    //
    // `loadAppearance` is the character CREATOR's save — skin, hair, clothes. It
    // knows nothing about gear, so the player walked into the arena in his shirt
    // while bots below rolled crafted plate at 45%: the one fighter whose armour
    // was earned was the only one not wearing any. Owner: "If i have armor, i
    // should appear in arena with armor which i have."
    //
    // These tiers do two jobs: they dress the paper-doll here, and they are the
    // player's loadout in `combat.ts gearedDamage` below — a sword adds, armour
    // soaks, both capped (owner: "crafted gear affect arena"). Failing to read them
    // must not stop the match, so a dead call just means bare-handed.
    const worn = await craftingApi.status()
      .then((st) => ({
        weapon: st.tiers.sword ?? 0,
        helmet: st.tiers.helmet ?? 0, chest: st.tiers.chest ?? 0, legs: st.tiers.legs ?? 0,
      }))
      .catch(() => ({ ...NO_GEAR }));
    playerGear = worn;
    const base = loadAppearance(ctx.user.username || ctx.user.email || 'p') ?? defaultAppearance();
    const playerLook = withArmor(base, worn.helmet, worn.chest, worn.legs);
    const all: { id: string; nickname: string; isPlayer: boolean; look: Appearance }[] = fighters.map((f) => {
      if (f.isPlayer) return { id: f.id, nickname: f.nickname, isPlayer: true, look: playerLook };
      // Look is rolled from the SLOT, not from a bot ordinal, so a slot keeps the
      // same character whether a human or a bot ends up in it.
      const rr = mulberry32(seed * 131 + f.slot * 977);
      const look = randomAppearance();
      // ONE roll decides what this bot WEARS and what it FIGHTS with. The two used
      // to be unrelated — a cosmetic 45% plate roll here, tier profiles for the
      // combat — and now that gear changes a fight, splitting them would show
      // armour that does nothing and hide armour that does.
      const gear = rollBotGear(rr);
      botGear.set(f.id, gear);
      const dressed = withArmor(look, gear.helmet, gear.chest, gear.legs);
      // Remote humans are rendered and fought like any other opponent: the match runs
      // client-side from the shared seed, so this client has no input stream for
      // them and drives their fighter with the same AI the bots use. What the
      // backfill changes is WHO the field is made of and the name over their head —
      // not who computes the fight.
      return { id: f.id, nickname: f.nickname, isPlayer: false, look: dressed };
    });
    // Resilient build: one character failing to compose (a missing equipment layer,
    // a load hiccup) must NOT reject the whole batch — that used to leave heroesReady
    // false forever, freezing the match in 'appear' (no movement, no bots). Each hero
    // retries with a plain appearance, then falls back to null and is skipped.
    const safeBuild = async (opts: Parameters<typeof createArenaHero>[0]): Promise<ArenaHero | null> => {
      try { return await createArenaHero(opts); }
      catch {
        try { return await createArenaHero({ ...opts, appearance: defaultAppearance() }); }
        catch (e) { console.error('[arena] hero build failed', opts.id, e); return null; }
      }
    };
    // The central tower footprint is UNWALKABLE, and the old inner radius (~2.6t)
    // dropped heroes right on top of it — so the player (and ~1/3 of bots) spawned
    // boxed in on all sides and could never move. Push the ring outside the tower
    // AND snap every spawn to the nearest walkable tile so no hero starts stuck.
    const built = await Promise.all(all.map((h, i) => {
      const ang = (i / all.length) * Math.PI * 2;
      const rad = TOWER_CLEAR_TILES + (r - TOWER_CLEAR_TILES) * (0.35 + 0.65 * ((i % 3) / 2));
      const rawTx = Math.round(cx + Math.cos(ang) * rad), rawTy = Math.round(cy + Math.sin(ang) * rad);
      const spot = nearestWalkable(rawTx, rawTy) ?? { x: rawTx, y: rawTy };
      return safeBuild({
        id: h.id, nickname: h.nickname, isPlayer: h.isPlayer,
        tx: spot.x, ty: spot.y,
        appearance: h.look, maxHp: 100,
      });
    }));
    if (!alive || !field) { for (const h of built) h?.destroy(); return; }
    built.forEach((h, idx) => {
      if (!h) return;   // this character failed to compose — skip it, keep the match alive
      // The animated character sprite + its nickname/HP overlay both go in the y-sorted layer.
      field.heroLayer.addChild(h.handle.sprite);
      field.heroLayer.addChild(h.overlay);
      heroes.push(h);
      // AI + combat profile: the player is a balanced regular; bots draw from the field.
      const prng = mulberry32(((seed * 2654435761) ^ ((idx + 1) * 40503)) >>> 0);
      const tier: SkillTier = h.isPlayer ? 'regular' : rollTier(prng);
      const pers: Personality = h.isPlayer ? 'tactician' : PERSONALITIES[Math.floor(prng() * PERSONALITIES.length)];
      const profile = deriveProfile(tier, pers, prng);
      combat.set(h.id, {
        profile,
        gear: h.isPlayer ? playerGear : (botGear.get(h.id) ?? NO_GEAR),
        kills: 0, pendingGold: 0, goldBanked: 0, streak: 0,
        safeCd: ARENA.SAFE_COOLDOWN_MS + Math.floor(prng() * 40_000), attackCd: 0, aiCd: Math.floor(prng() * AI_TICK_MS), campMs: 0,
        mem: newBotMemory(profile.reactionMs, Math.floor(prng() * AI_TICK_MS)),
        px: 0, py: 0, moving: false,
        wasInSafe: false, deathOrder: 0, timeAlive: 0, entryOrder: idx, attackTargetId: null, rng: prng,
        goalTx: 0, goalTy: 0, hasGoal: false, stuckMs: 0, wanderCd: 0, stallMs: 0,
      });
      if (h.isPlayer) { player = h; camTarget = h; }
    });
    heroesReady = true;
    if (restore) applyRestore(restore);
  }

  /**
   * Put the fight back exactly where the reload found it.
   *
   * Runs AFTER the ordinary seed-derived build, so everything the seed owns (field,
   * nav, roster, appearances, bot gear, AI profiles) is already correct and identical
   * to the match the player left; this only overwrites what a seed cannot know.
   *
   * The clocks then jump forward by the wall-clock gap WITHOUT simulating it. Nobody
   * is stepped across the gap on purpose: battleStep depends on async nav requests and
   * on the per-hero RNG that a snapshot cannot carry, so a catch-up sim would diverge
   * from the very state it is meant to continue — and would take longer than the
   * reload did. Under MATCH_CATCHUP_MS the honest description is "the clock moved, the
   * world did not"; past it the snapshot is refused outright (matchResumeAction).
   */
  function applyRestore(snap: MatchSnapshot) {
    const hs = new Map<string, HeroSnap>(snap.heroes.map((h) => [h.id, h]));
    const cs = new Map<string, CombatSnap>(snap.combat.map((c) => [c.id, c]));
    for (const h of heroes) {
      const sh = hs.get(h.id);
      if (!sh) continue;
      h.handle.setPos(sh.x, sh.y);
      h.setHp(sh.hp);
      h.syncOverlay();
      // A hero who was already dead is despawned the same way the fight despawns
      // them, so the live counter, the spectate picker and the scoring all agree.
      if (!sh.alive) void h.kill();
      const c = combat.get(h.id), sc = cs.get(h.id);
      if (!c || !sc) continue;
      c.kills = sc.kills; c.pendingGold = sc.pendingGold; c.goldBanked = sc.goldBanked;
      c.streak = sc.streak; c.safeCd = sc.safeCd; c.attackCd = sc.attackCd; c.aiCd = sc.aiCd;
      c.campMs = sc.campMs; c.wasInSafe = sc.wasInSafe;
      c.deathOrder = sc.deathOrder; c.timeAlive = sc.timeAlive; c.entryOrder = sc.entryOrder;
      c.attackTargetId = sc.attackTargetId;
      c.goalTx = sc.goalTx; c.goalTy = sc.goalTy; c.hasGoal = sc.hasGoal;
      c.stuckMs = sc.stuckMs; c.wanderCd = sc.wanderCd; c.stallMs = sc.stallMs;
    }
    deaths = snap.deaths;
    battleClock = snap.battleClock + restoreGap;
    resultSent = snap.resultSent;
    rosterPanel.remove();
    phase = snap.phase as Phase;
    phaseT = snap.phaseT + restoreGap;
    if (phase === 'battle') phaseLabel.textContent = 'BATTLE';
    toast('Match restored — you were reconnected');
  }

  /**
   * Snapshot the live match so a reload can hand it back (defect 19, rule R3).
   *
   * Cheap enough to run at 1 Hz: 25 heroes of rounded numbers, ~20 KB of JSON against
   * a 5 MB quota. Rounded on purpose — sub-pixel positions cost bytes and buy nothing.
   */
  function saveSnapshot() {
    if (!alive || resultSent || phase === 'end') return;
    // NOTHING TO SAVE UNTIL THE FIGHTERS EXIST — and on a restore, saving here is
    // actively destructive. `heroes`/`combat` stay empty until placeHeroes finishes
    // composing 25 characters (seconds), but the ticker starts on mount, so the 1 Hz
    // write would flatten the very snapshot being restored from into an empty one
    // and the fight would come back as an empty arena.
    if (!heroesReady || !heroes.length) return;
    const r = Math.round;
    writeMatchSnapshot({
      v: 2, seed, seats: [...(seats?.length ? seats : soloSeats(youName, HERO_COUNT))],
      paid: paidEntry, matchId,
      phase, phaseT: r(phaseT), phaseDurMs: phaseDur(phase),
      battleClock: r(battleClock), deaths, resultSent, savedAt: Date.now(),
      heroes: heroes.map((h) => ({ id: h.id, x: r(h.x), y: r(h.y), hp: r(h.hp), alive: h.alive })),
      combat: [...combat.entries()].map(([id, c]) => ({
        id,
        kills: c.kills, pendingGold: c.pendingGold, goldBanked: c.goldBanked, streak: c.streak,
        safeCd: r(c.safeCd), attackCd: r(c.attackCd), aiCd: r(c.aiCd), campMs: r(c.campMs),
        wasInSafe: c.wasInSafe,
        deathOrder: c.deathOrder, timeAlive: r(c.timeAlive), entryOrder: c.entryOrder,
        attackTargetId: c.attackTargetId,
        goalTx: c.goalTx, goalTy: c.goalTy, hasGoal: c.hasGoal,
        stuckMs: r(c.stuckMs), wanderCd: r(c.wanderCd), stallMs: r(c.stallMs),
      })),
    });
  }

  // Center on a tile, CLAMPED so the map always fills the screen — never any black
  // void past the field edge (the whole point of the enclosing border).
  function centerOn(txPx: number, tyPx: number) {
    if (!worldC || !app) return;
    const mapW = field.cols * ARENA_TILE, mapH = field.rows * ARENA_TILE;
    const sw = app.screen.width, sh = app.screen.height;
    let x = sw / 2 - txPx * ARENA_TILE, y = sh / 2 - tyPx * ARENA_TILE;
    // Clamp so [0, mapW] covers [0, sw]; if the map is smaller than the screen, center it.
    x = mapW >= sw ? Math.min(0, Math.max(sw - mapW, x)) : (sw - mapW) / 2;
    y = mapH >= sh ? Math.min(0, Math.max(sh - mapH, y)) : (sh - mapH) / 2;
    worldC.x = x; worldC.y = y;
  }

  let resultsShown = false;
  /** Pending hand-back of the bed after the victory sting (see showResults). */
  let stingTimer: number | null = null;
  function setPhase(p: Phase) {
    phase = p; phaseT = 0;
    if (p === 'appear') rosterPanel.remove();
    if (p === 'battle') { phaseLabel.textContent = 'BATTLE'; }
    // Prep is the first beat where the player can actually move, so it is where the
    // guide opens. It deliberately runs on into the battle — prep is only 2.5s and
    // every step is dismissible, so the alternative is a card nobody can read.
    // Never on a restore: the guide teaches the opening beats, and a player who is
    // being handed back a fight already in progress has seen them.
    if (p === 'prep' && !restore && !tour && !arenaTutorialSeen() && player) {
      tour = startArenaTour(wrap, {
        field: () => field ?? null,
        player: () => player,
        worldC: () => worldC,
        banked: () => (player ? combat.get(player.id)?.goldBanked ?? 0 : 0),
        hud: { timer: topBar, alive: liveCounter, gold: goldHud, controls: controlsHint },
      });
    }
    // Retire the card before the results overlay mounts — both sit at z-[60].
    if (p === 'end') { showResults(); }
    // Every transition is a cheap, natural save point (and 'end' clears instead).
    saveSnapshot();
  }

  /**
   * Show the 3 result screens from the LIVE match state (the rendered heroes ran the
   * real AI + combat), relabelling the player's row as "you". If the battle never
   * started (left early), fall back to the deterministic sim for the seed.
   */
  function showResults(onKeepWatching?: () => void) {
    if (resultsShown) return;
    resultsShown = true;
    // Retire the guide before the overlay mounts — both sit at z-[60], and dying
    // early reaches showResults() without ever passing through setPhase('end').
    tour?.destroy(); tour = null;
    const you = ctx.user.username || 'You';
    let results: HeroResult[];
    if (combat.size && player) {
      results = buildLiveResults().map((r) => (r.heroId === player!.id ? { ...r, heroId: you } : r));
    } else {
      results = simulateMatch(seed, { withPlayer: true }).results.map((r) => (/_0$/.test(r.heroId) ? { ...r, heroId: you } : r));
    }
    const mine = results.find((r) => r.heroId === you);
    // Submit to the server: it validates/clamps the claim and updates rating. Banked
    // gold is score only — nothing is paid out for it.
    if (mine && !resultSent) {
      resultSent = true;
      // The match is REPORTED: from here a snapshot could only resurrect a finished
      // fight, so the record goes now. This clear is the ONLY thing preventing a
      // second result — do not weaken it on the assumption that the server dedupes.
      // The server now refuses a second result for the same matchId (it resolves the
      // ArenaBrMatch row on the first one), but this clear stays: it is what stops a
      // finished fight coming back at all, which is a better outcome than one that
      // comes back and then fails to submit.
      clearMatchSnapshot();
      void arenaBrApi.result({ matchId, placement: mine.survivalPlace, kills: mine.kills, goldBanked: mine.goldBanked })
        .then(() => ctx.refreshHud())
        .catch(() => undefined);
    }
    // The win fanfare. It is the ONLY thing victory.m4a is for, so it plays on a win
    // and nothing else — a fanfare over "ELIMINATED, 19 of 25" would read as mockery.
    // Routed through the bed (a scene change, not a raw <audio>) so it obeys the
    // player's music volume and mute like everything else, and so the dense arena bed
    // cross-fades OUT under it instead of fighting it. Handed back after the sting's
    // length because beds loop and this one is eight seconds long.
    if (mine?.survivalPlace === 1) {
      setAudioOverlay('arena-result');
      stingTimer = window.setTimeout(() => {
        stingTimer = null;
        // Back to the battle bed, not to silence: "Keep watching" leaves a live match
        // running underneath. Teardown clears the overlay for good.
        if (alive) setAudioOverlay('arena-match');
      }, VICTORY_STING_MS);
    }
    const nameMap = new Map(heroes.map((h) => [h.id, h.nickname]));
    // Mounted INSIDE the match subtree (not on document.body): the teardown below
    // removes the arena wrap, and a body-mounted overlay survived it — a full-screen
    // results card left hanging over whatever view you switched to.
    closeResults?.();
    closeResults = showResultScreens(wrap, results, you, () => { closeResults = null; onFinish?.(); }, nameMap, onKeepWatching);
  }

  const fmt = (ms: number) => {
    const s = Math.ceil(ms / 1000);
    return s >= 60 ? `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}` : String(s);
  };

  /** ms since the last snapshot — the 1 Hz cadence rides the existing frame loop. */
  let snapAcc = 0;
  /**
   * How often the live match is snapshotted from the ticker.
   *
   * Was 1_000, and saveSnapshot is SYNCHRONOUS: JSON.stringify of 25 heroes plus
   * their combat state and a sessionStorage.setItem, on the main thread, inside the
   * frame — a visible hitch every single second on a mid-range Android. Five seconds
   * costs a resumed fight up to 5s of rewind, which the restore's clock catch-up
   * already absorbs and no player can tell apart from the reload itself.
   *
   * The saves that actually matter are not on this timer anyway: pagehide and
   * visibilitychange both save immediately, and those are the paths a reload, a tab
   * switch or a backgrounded phone actually take.
   */
  const SNAP_EVERY_MS = 5_000;

  function tick(dtMs: number) {
    // Debug (press K): freeze the BATTLE timer so you can roam the whole map without
    // the match ending. Movement + combat still run; only the countdown/end is paused.
    if (!(freezeTimer && phase === 'battle')) phaseT += dtMs;
    syncLeaveGuard();   // the fight starting, ending, or the player dying all change the answer
    const remain = phaseDur(phase) - phaseT;
    if (field) field.animate(dtMs);   // §6.2: lava bubbles + torch flames animate every frame

    switch (phase) {
      case 'lineup':
        phaseLabel.textContent = 'WARRIORS GATHERING…';
        bigTimer.textContent = '';
        revealRoster(phaseT);   // §6.8: opponents "connect" one by one
        if (remain <= 0) setPhase('countdown');
        break;
      case 'countdown':
        phaseLabel.textContent = 'GET READY — dropping in';
        bigTimer.textContent = fmt(remain);
        if (remain <= 0) setPhase('appear');
        break;
      case 'appear': {
        // Never a blank screen: while the 25 characters finish composing, say so.
        phaseLabel.textContent = heroesReady ? '' : 'Entering the arena…';
        bigTimer.textContent = '';
        if (!heroesReady) break;   // wait for the composed characters to finish loading
        for (const h of heroes) h.syncOverlay();
        if (remain <= 0) setPhase('prep');
        break;
      }
      case 'prep': {
        const locked = phaseT < PREP_LOCK_MS;
        // You can move now — walk to study the map / take position. Fight starts at 0.
        phaseLabel.textContent = locked ? 'MOVE — study the map · fight in' : 'TAKE POSITION · fight in';
        bigTimer.textContent = fmt(remain);
        // Milling in the safe space (damage still off) — no combat yet.
        wanderStep(dtMs / 1000, locked);
        if (remain <= 0) setPhase('battle');
        break;
      }
      case 'battle':
        phaseLabel.textContent = 'BATTLE';
        bigTimer.textContent = fmt(remain);
        // The rendered heroes now run the real AI + combat (same rules as the sim).
        battleStep(dtMs / 1000, remain);
        // Ends at last-hero-standing or time expiry.
        if (!freezeTimer && (remain <= 0 || heroes.filter((h) => h.alive).length <= 1)) setPhase('end');
        break;
      case 'end':
        phaseLabel.textContent = 'MATCH OVER';
        bigTimer.textContent = '';
        break;
    }

    // On the ticker that is already running — a second timer for this would keep the
    // tab awake for nothing. Worst-case rewind is SNAP_EVERY_MS, which the restore's
    // clock catch-up absorbs; the saves that matter are the pagehide/visibilitychange
    // ones above, which fire the instant the page is actually leaving.
    snapAcc += dtMs;
    if (snapAcc >= SNAP_EVERY_MS) { snapAcc = 0; saveSnapshot(); }

    const aliveCount = heroes.filter((h) => h.alive).length || HERO_COUNT;
    liveCounter.textContent = `${aliveCount} alive`;
    controlsHint.classList.toggle('hidden', !(phase === 'prep' || phase === 'battle'));
    followCam();
  }

  // Clamp the world container so the map always fills the screen (no black borders).
  function clampCam(nx: number, ny: number) {
    if (!worldC || !app) return;
    const sw = app.screen.width, sh = app.screen.height;
    const mapW = field.cols * ARENA_TILE, mapH = field.rows * ARENA_TILE;
    worldC.x = mapW >= sw ? Math.min(0, Math.max(sw - mapW, nx)) : (sw - mapW) / 2;
    worldC.y = mapH >= sh ? Math.min(0, Math.max(sh - mapH, ny)) : (sh - mapH) / 2;
  }

  // Deadzone follow: the camera tracks `camTarget` (the player while alive; a clicked
  // hero when spectating) unless the viewer has grabbed the map (freeCam). Only scrolls
  // once the target pushes the deadzone edge, so movement reads on screen.
  function followCam() {
    if (!worldC || !app || freeCam) return;
    const t = camTarget && camTarget.alive ? camTarget : (player && player.alive ? player : null);
    if (!t) return;                                        // dead + no spectate pick → hold the view
    // Keep the tracked hero CENTRED (owner: camera moves WITH the user, no deadzone).
    // Smoothed toward centre so it glides instead of snapping every frame.
    const sw = app.screen.width, sh = app.screen.height;
    const wantX = sw / 2 - t.x, wantY = sh / 2 - t.y;
    clampCam(worldC.x + (wantX - worldC.x) * 0.2, worldC.y + (wantY - worldC.y) * 0.2);
  }

  // Nearest living hero to a screen point (for click-to-spectate when dead).
  function heroUnderPoint(gx: number, gy: number): ArenaHero | null {
    if (!worldC) return null;
    const wx = gx - worldC.x, wy = gy - worldC.y;
    let best: ArenaHero | null = null, bd = Infinity;
    for (const h of heroes) {
      if (!h.alive) continue;
      const d = Math.hypot(h.x - wx, h.y - wy);
      if (d < bd && d < TILE * 1.3) { bd = d; best = h; }
    }
    return best;
  }

  // ─── Boot Pixi ────────────────────────────────────────────────────────────
  // A restored match never shows the "warriors gathering" roster card: that beat is
  // over, and leaving it up would read as the fight starting again from the top.
  if (restore) rosterPanel.remove(); else paintRoster();
  (async () => {
    const application = new Application();
    // §6.1: match the farm's renderer exactly. The farm forces the WebGL backend
    // (preference:'webgl'); without it Pixi auto-picks WebGPU here, which rendered
    // the pixel-art blurrier than the farm. All other init options mirror the farm.
    await application.init({
      width: wrap.clientWidth || 820, height: wrap.clientHeight || 520,
      background: '#150d14', antialias: false, preference: 'webgl',
      // Render at device pixel density (capped at 2) + autoDensity so the nickname
      // TEXT is crisp instead of blurry — Pixi rasterises text at the renderer
      // resolution, which defaulted to 1 and got upscaled on retina screens.
      resolution: Math.min(window.devicePixelRatio || 1, 2), autoDensity: true,
    });
    if (!alive) { application.destroy(); return; }
    app = application;
    stageHost.appendChild(app.canvas);
    // Build the grass battlefield + nav, then the pannable world (ground + heroes).
    field = await buildArenaField(seed);
    if (!alive || !app) { field.destroy(); return; }
    for (const el of field.layout.els) if (el.kind === 'lava') lavaTiles.add(el.ty * field.cols + el.tx);
    fnav = buildFieldNav(field);
    navService = new NavService(fnav.grid);
    worldC = new Container();
    worldC.addChild(field.root);          // grass + static decor
    worldC.addChild(field.heroLayer);     // trees/rocks + heroes (y-sorted)
    app.stage.addChild(worldC);
    // Nav debug overlay (§9) — pans with the world. Toggle P, cycle heatmap with H.
    debug = createArenaDebug(fnav, field.cols);
    worldC.addChildAt(debug.layer, 1);
    // Walkability overlay (toggle B): GREEN = you can walk here, RED = blocked. This
    // is the single source of truth (bridge decks over lava read GREEN; bare lava and
    // coastline read RED), so we can see instantly whether a bridge is walkable.
    gridDbg = new Graphics();
    for (let ty = 0; ty < field.rows; ty++) for (let tx = 0; tx < field.cols; tx++) {
      const blocked = fnav.grid.walkable[tileIndex(tx, ty, field.cols)] === 0;
      gridDbg.rect(tx * TILE + 1, ty * TILE + 1, TILE - 2, TILE - 2)
        .fill({ color: blocked ? 0xff2020 : 0x30e070, alpha: blocked ? 0.32 : 0.24 });
    }
    gridDbg.visible = false;
    worldC.addChild(gridDbg);
    centerOn(field.safe.cx, field.safe.cy);
    // Pointer: DRAG the map to pan/look around (spectate); a plain TAP either
    // click-to-moves the living player or, when dead/spectating, follows the clicked
    // hero. Drag vs tap is decided by how far the pointer moved between down and up.
    app.stage.eventMode = 'static';
    app.stage.hitArea = { contains: () => true } as { contains: () => boolean };
    let dragFrom: { gx: number; gy: number; wx: number; wy: number } | null = null;
    let dragMoved = false;
    app.stage.on('pointerdown', (e) => {
      if (!worldC) return;
      dragFrom = { gx: e.global.x, gy: e.global.y, wx: worldC.x, wy: worldC.y };
      dragMoved = false;
    });
    app.stage.on('pointermove', (e) => {
      if (!dragFrom || !worldC) return;
      const dx = e.global.x - dragFrom.gx, dy = e.global.y - dragFrom.gy;
      if (!dragMoved && Math.abs(dx) + Math.abs(dy) < 8) return;   // still a tap
      dragMoved = true;
      freeCam = true;                                    // grabbing the map stops auto-follow
      clampCam(dragFrom.wx + dx, dragFrom.wy + dy);
    });
    const endDrag = (e: { global: { x: number; y: number } }) => {
      const wasDrag = dragMoved; dragFrom = null; dragMoved = false;
      if (wasDrag || !worldC) return;                    // a pan, not a click
      const canControl = (phase === 'prep' || phase === 'battle') && player && player.alive;
      if (canControl) {
        // Clicked an ENEMY → engage it: lock the target and walk into range. The
        // swing itself still runs on PLAYER_ATTACK_CD_MS, so holding the mouse down
        // on someone is exactly as fast as clicking once and standing there.
        const foe = heroUnderPoint(e.global.x, e.global.y);
        if (foe && foe !== player && foe.alive) {
          clickTargetId = foe.id;
          heldKeys.clear();
          requesting.delete(player!.id);
          player!.interrupt();
          const ft = tileOf(foe);
          goTo(player!, ft.tx, ft.ty);
          freeCam = false; camTarget = player;
          return;
        }
        clickTargetId = null;   // clicked the ground: stop chasing whoever it was
        // Living player → PATHFIND to the clicked tile so the route goes AROUND lava
        // and ACROSS bridges (a straight target used to walk into the lava and stop).
        // goTo snaps to the nearest walkable tile and requests a nav path.
        const wtx = (e.global.x - worldC.x) / ARENA_TILE, wty = (e.global.y - worldC.y) / ARENA_TILE;
        heldKeys.clear();
        requesting.delete(player!.id);
        player!.interrupt();
        goTo(player!, Math.floor(wtx), Math.floor(wty));
        freeCam = false; camTarget = player;
      } else {
        // Dead / intro / spectating → click a fighter to follow them.
        const h = heroUnderPoint(e.global.x, e.global.y);
        if (h) { camTarget = h; freeCam = false; toast(`Spectating ${h.nickname}`); }
      }
    };
    app.stage.on('pointerup', endDrag);
    app.stage.on('pointerupoutside', endDrag);
    // Touch: a thumbstick moves the player hero (combat auto-targets the nearest
    // enemy in range, so movement is the control that matters). Feeds the same
    // heldDir path the keyboard uses.
    if (isTouchDevice()) {
      joystick = createJoystick(wrap, (vx, vy) => {
        joyVec = (vx || vy) ? { x: vx, y: vy } : null;
        if (!joyVec && player) player.setKeys(0, 0);
      });
    }
    // compose the 25 characters while the lineup + countdown play. Never let a
    // failure leave the phase machine stuck before 'prep' — force-ready on error.
    void placeHeroes().catch((e) => { console.error('[arena] placeHeroes failed', e); heroesReady = true; });
    // rAF drives the match while visible; when the tab is hidden rAF is paused, so a
    // timer advances the sim by real elapsed time (≤100ms slices) and forces a render.
    app.ticker.add((t) => { if (alive && !document.hidden) tick(Math.min(t.deltaMS, 100)); });
    lastHiddenTs = performance.now();
    bgTimer = window.setInterval(() => {
      if (!alive || !document.hidden || !app) return;
      const now = performance.now();
      let dt = Math.min(now - lastHiddenTs, 1000);   // cap a long sleep to one 1s catch-up
      lastHiddenTs = now;
      while (dt > 0) { tick(Math.min(dt, 100)); dt -= 100; }
      app.renderer.render(app.stage);
    }, 200);
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('pagehide', onPageHide);
  })();

  const MOVE_KEYS = new Set(['w', 'a', 's', 'd', 'ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight']);
  const onKey = (e: KeyboardEvent) => {
    // Ignore game keys while the player is typing in an input (e.g. world chat).
    const ae = document.activeElement as HTMLElement | null;
    if (ae && (ae.tagName === 'INPUT' || ae.tagName === 'TEXTAREA' || ae.isContentEditable)) return;
    if (e.key === 'p' || e.key === 'P') { const on = debug?.toggle(); phaseLabel.dataset.debug = on ? '1' : ''; }
    else if (e.key === 'h' || e.key === 'H') debug?.cycleHeatmap();
    else if (e.key === 'k' || e.key === 'K') { freezeTimer = !freezeTimer; toast(freezeTimer ? 'Timer frozen — roam freely (K)' : 'Timer resumed'); }
    else if (e.key === 'b' || e.key === 'B') {
      if (gridDbg) {
        gridDbg.visible = !gridDbg.visible;
        dbg.classList.toggle('hidden', !gridDbg.visible);   // the tile readout explains the overlay
        toast(gridDbg.visible ? 'Walkability overlay ON — GREEN=walkable, RED=blocked (B)' : 'Overlay off');
      }
    }
    else if (MOVE_KEYS.has(e.key)) { heldKeys.add(e.key); player?.interrupt(); }
  };
  const onKeyUp = (e: KeyboardEvent) => { if (MOVE_KEYS.has(e.key)) { heldKeys.delete(e.key); if (player && !heldDir()) player.setKeys(0, 0); } };
  window.addEventListener('keydown', onKey);
  window.addEventListener('keyup', onKeyUp);

  return () => {
    alive = false;
    clearLeaveGuard();
    // The view is going for real (tab switch, forfeit, results dismissed) — this is an
    // ENDING, not a reload, and an ending must never leave a snapshot behind that could
    // resurrect the fight on the next mount. A page refresh does not run this.
    clearMatchSnapshot();     // the view is going; the prompt must not outlive it
    // WALKING OUT OF A LIVE MATCH IS A FORFEIT.
    //
    // Nothing was submitted when a match was abandoned, so leaving cost the entry
    // and nothing else — you could bail out of a losing fight and take no rating
    // hit at all, which makes the ladder meaningless and is the other half of the
    // owner's "for each fight we should create pts". A forfeit is scored as last
    // place with no kills and no gold, which is what leaving actually is.
    //
    // Only while the fight is real: the lineup, the results screen and a match the
    // player has already died out of have either not started or already reported.
    if (!resultSent && player?.alive && (phase === 'prep' || phase === 'battle')) {
      resultSent = true;
      void arenaBrApi.result({ matchId, placement: heroes.length, kills: 0, goldBanked: 0 })
        .then(() => ctx.refreshHud())
        .catch(() => undefined);
    }
    closeResults?.(); closeResults = null;
    // Hand music ownership back to whatever tab is underneath — the farm reclaims
    // its day/night bed here, the lobby goes quiet.
    if (stingTimer !== null) { window.clearTimeout(stingTimer); stingTimer = null; }
    setAudioOverlay(null);
    if (bgTimer) window.clearInterval(bgTimer);
    document.removeEventListener('visibilitychange', onVisibility);
    window.removeEventListener('pagehide', onPageHide);
    window.removeEventListener('keydown', onKey);
    window.removeEventListener('keyup', onKeyUp);
    for (const h of heroes) h.destroy();
    joystick?.destroy();
    // Silent teardown: destroy() does NOT mark the tour done, so a match the player
    // walked out of replays the guide next time — which is what they need.
    tour?.destroy();
    debug?.destroy();
    worldC?.destroy({ children: true });
    app?.destroy(true);
  };
}
